import type React from "react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

type EventHandler = (data: unknown) => void;

interface SSEContextType {
	addEventHandler: (eventName: string, handler: EventHandler) => void;
	removeEventHandler: (eventName: string, handler: EventHandler) => void;
	disconnect: () => void;
	reconnect: () => void;
	isConnected: boolean;
	clientId: string | null;
	connectionState: "connecting" | "connected" | "disconnected" | "error";
}

const SSEContext = createContext<SSEContextType | undefined>(undefined);

// Generate unique client ID
function generateClientId(): string {
	return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface SSEProviderProps {
	children: ReactNode;
	autoConnect?: boolean;
	maxReconnectAttempts?: number;
	reconnectDelay?: number;
	sseEndpoint?: string;
}

export const SSEProvider: React.FC<SSEProviderProps> = ({
	children,
	autoConnect = true,
	maxReconnectAttempts = 5,
	reconnectDelay = 1000,
	sseEndpoint = "/sse",
}) => {
	const [clientId, setClientId] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const [connectionState, setConnectionState] = useState<
		"connecting" | "connected" | "disconnected" | "error"
	>("disconnected");

	const eventSourceRef = useRef<EventSource | null>(null);
	const handlersRef = useRef<Record<string, EventHandler[]>>({});
	const eventListenersRef = useRef<Record<string, (e: MessageEvent) => void>>(
		{},
	);
	const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const reconnectAttemptsRef = useRef<number>(0);
	const isManuallyDisconnectedRef = useRef<boolean>(false);

	// Initialize client ID on mount
	useEffect(() => {
		const stored = sessionStorage.getItem("sseClientId");
		const id = stored || generateClientId();
		if (!stored) {
			sessionStorage.setItem("sseClientId", id);
		}
		setClientId(id);
	}, []);

	const addEventHandler = useCallback(
		(eventName: string, handler: EventHandler) => {
			if (!handlersRef.current[eventName]) {
				handlersRef.current[eventName] = [];
			}
			handlersRef.current[eventName].push(handler);

			// Add event listener to EventSource if it exists and this is the first handler for this event
			const eventSource = eventSourceRef.current;
			if (
				eventSource &&
				handlersRef.current[eventName].length === 1 &&
				eventName !== "message"
			) {
				const listener = (e: MessageEvent) => {
					try {
						const data = JSON.parse(e.data);
						const handlers = handlersRef.current[eventName] || [];
						for (const h of handlers) {
							h(data);
						}
					} catch (error) {
						console.error(`Error parsing SSE event ${eventName}:`, error);
					}
				};
				eventListenersRef.current[eventName] = listener;
				eventSource.addEventListener(eventName, listener);
			}
		},
		[],
	);

	const removeEventHandler = useCallback(
		(eventName: string, handler: EventHandler) => {
			if (handlersRef.current[eventName]) {
				handlersRef.current[eventName] = handlersRef.current[eventName].filter(
					(h) => h !== handler,
				);

				// Remove event listener if no more handlers exist
				const eventSource = eventSourceRef.current;
				if (
					handlersRef.current[eventName].length === 0 &&
					eventSource &&
					eventName !== "message"
				) {
					const listener = eventListenersRef.current[eventName];
					if (listener) {
						eventSource.removeEventListener(eventName, listener);
						delete eventListenersRef.current[eventName];
					}
				}
			}
		},
		[],
	);

	const cleanupConnection = useCallback(() => {
		// Clear reconnection timeout
		if (reconnectTimeoutRef.current) {
			clearTimeout(reconnectTimeoutRef.current);
			reconnectTimeoutRef.current = null;
		}

		// Clean up all event listeners
		for (const [eventName, listener] of Object.entries(
			eventListenersRef.current,
		)) {
			eventSourceRef.current?.removeEventListener(eventName, listener);
		}
		eventListenersRef.current = {};

		// Close connection
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}

		setIsConnected(false);
		setConnectionState("disconnected");
	}, []);

	const connectEventSource = useCallback(
		(id: string) => {
			// Clean up existing connection
			cleanupConnection();

			if (isManuallyDisconnectedRef.current) {
				return;
			}

			setConnectionState("connecting");

			const fullUrl = `${sseEndpoint}?clientId=${id}`;
			const eventSource = new EventSource(fullUrl, {
				withCredentials: true,
			});

			eventSourceRef.current = eventSource;

			// Handle open connection
			eventSource.onopen = () => {
				setIsConnected(true);
				setConnectionState("connected");
				reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
				console.log("SSE connection opened");
			};

			// Handle errors with reconnection logic
			eventSource.onerror = (e) => {
				setIsConnected(false);
				setConnectionState("error");
				console.error("SSE error:", e);

				// Only attempt reconnection if we haven't exceeded max attempts and not manually disconnected
				if (
					reconnectAttemptsRef.current < maxReconnectAttempts &&
					!isManuallyDisconnectedRef.current
				) {
					const delay = reconnectDelay * 2 ** reconnectAttemptsRef.current; // Exponential backoff
					console.log(
						`Attempting to reconnect in ${delay}ms (attempt ${
							reconnectAttemptsRef.current + 1
						}/${maxReconnectAttempts})`,
					);

					reconnectTimeoutRef.current = setTimeout(() => {
						reconnectAttemptsRef.current += 1;
						connectEventSource(id);
					}, delay);
				} else if (!isManuallyDisconnectedRef.current) {
					console.error("Max reconnection attempts reached");
					setConnectionState("error");
				}
			};

			// Handle connected event
			eventSource.addEventListener("connected", (e) => {
				try {
					const data = JSON.parse(e.data);
					if (data.clientId && data.clientId !== id) {
						console.log("Connected with client ID:", data.clientId);
						sessionStorage.setItem("sseClientId", data.clientId);
						setClientId(data.clientId);
					}
				} catch (error) {
					console.error("Error parsing connected event:", error);
				}
			});

			// Handle general messages
			eventSource.onmessage = (e) => {
				try {
					const data = JSON.parse(e.data);
					console.log("SSE message received:", data);
					const handlers = handlersRef.current.message || [];
					for (const handler of handlers) {
						handler(data);
					}
				} catch (error) {
					console.error("Error parsing SSE message:", error);
				}
			};

			// Add existing event listeners for custom events
			for (const [eventName, handlers] of Object.entries(handlersRef.current)) {
				if (eventName !== "message" && handlers.length > 0) {
					const listener = (e: MessageEvent) => {
						try {
							const data = JSON.parse(e.data);
							for (const handler of handlers) {
								handler(data);
							}
						} catch (error) {
							console.error(`Error parsing SSE event ${eventName}:`, error);
						}
					};
					eventListenersRef.current[eventName] = listener;
					eventSource.addEventListener(eventName, listener);
				}
			}
		},
		[cleanupConnection, maxReconnectAttempts, reconnectDelay, sseEndpoint],
	);

	// Main effect to establish connection
	useEffect(() => {
		if (!clientId || !autoConnect) return;

		isManuallyDisconnectedRef.current = false;
		connectEventSource(clientId);

		return cleanupConnection;
	}, [clientId, autoConnect, connectEventSource, cleanupConnection]);

	const disconnect = useCallback(() => {
		isManuallyDisconnectedRef.current = true;
		cleanupConnection();
	}, [cleanupConnection]);

	const reconnect = useCallback(() => {
		if (clientId) {
			isManuallyDisconnectedRef.current = false;
			reconnectAttemptsRef.current = 0; // Reset attempts
			connectEventSource(clientId);
		}
	}, [clientId, connectEventSource]);

	const contextValue: SSEContextType = {
		addEventHandler,
		removeEventHandler,
		disconnect,
		reconnect,
		isConnected,
		clientId,
		connectionState,
	};

	return (
		<SSEContext.Provider value={contextValue}>{children}</SSEContext.Provider>
	);
};

// Custom hook to use SSE context
export const useSSE = (): SSEContextType => {
	const context = useContext(SSEContext);
	if (context === undefined) {
		throw new Error("useSSE must be used within an SSEProvider");
	}
	return context;
};

// Higher-order component for class components
export const withSSE = <P extends object>(
	Component: React.ComponentType<P & { sse: SSEContextType }>,
) => {
	return (props: P) => {
		const sse = useSSE();
		return <Component {...props} sse={sse} />;
	};
};

// Hook for subscribing to specific events with automatic cleanup
export const useSSEEvent = (eventName: string, handler: EventHandler) => {
	const { addEventHandler, removeEventHandler } = useSSE();

	useEffect(() => {
		addEventHandler(eventName, handler);
		return () => removeEventHandler(eventName, handler);
	}, [eventName, handler, addEventHandler, removeEventHandler]);
};

// Hook for connection status
export const useSSEConnection = () => {
	const { isConnected, connectionState, disconnect, reconnect } = useSSE();
	return { isConnected, connectionState, disconnect, reconnect };
};

export default SSEProvider;
